import { useEffect, useMemo, useRef, useState } from "react";
// components
import { ReactComponent as OutlineEyeOpen } from "assets/svg/outline-eye-open.svg";
import { ReactComponent as OutlineSearchGray } from "assets/svg/outline-search-gray.svg";
import Loading from "components/Loading";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import { useUser } from "contexts/UserContext";
import moment from "moment-timezone";
import { Alert, ListGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import { PatientNote, queryPatientNotesAll } from "services/queryPatientNotes";
import getClasses from "utils/getClasses";
import styles from "./PreviousNoteList.module.scss";

const DATE_FORMAT = "MM/DD/YYYY";

const PreviousNotesList = ({
  patientNote,
  pickModelNote,
  selectedModelNote,
}: {
  patientNote?: PatientNote;
  pickModelNote: (arg: PatientNote) => void;
  selectedModelNote?: number[];
}) => {
  const { defaultUserTimezone } = useUser();

  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);

  const [noteSearchTerm, setNoteSearchTerm] = useState("");

  const [patientNotes, setPatientNotes] = useState<PatientNote[]>([]);

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    isInitialized.current = true;

    setBusy(true);
    queryPatientNotesAll({})
      .then((notesResult) => {
        const signedNotes = notesResult
          .filter((el) => el.sign_timestamp)
          .sort((a, b) =>
            new Date(a.sign_timestamp || 0) > new Date(b.sign_timestamp || 0)
              ? -1
              : 1,
          );
        setPatientNotes(signedNotes);
        if (signedNotes.length > 0) {
          if (selectedModelNote && selectedModelNote.length > 0) {
            const selectedSignedNote = signedNotes.find(
              (el) => el.note_data_id === selectedModelNote[0],
            );

            if (selectedSignedNote) pickModelNote(selectedSignedNote);
          } else {
            pickModelNote(signedNotes[0]);
          }
        }
      })
      .catch()
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchChange = (e) => {
    setNoteSearchTerm(e.currentTarget.value);
  };

  const notes = useMemo(() => {
    return patientNotes.filter(
      (note) =>
        noteSearchTerm.length === 0 ||
        note.note_schema_title
          ?.toUpperCase()
          .includes(noteSearchTerm.toUpperCase()),
    );
  }, [noteSearchTerm, patientNotes]);

  return (
    <div className={styles["previous-note"]}>
      {error ? <Alert variant="danger">{error.toString()}</Alert> : null}
      <div className={styles["previous-note__search-area"]}>
        <div className={"bd-16 mb-2"}>
          Select a previous note to use as your model note.
        </div>
        <div className={"rg-14"}>
          You need to create at least one submitted clinical note in Orchid to
          create a Model Note.
        </div>
        <div className={styles["previous-note__search"]}>
          <input
            className="form-control"
            type="text"
            style={{ display: "inline" }}
            value={noteSearchTerm}
            onChange={onSearchChange}
            placeholder="Search"
          />
          <OutlineSearchGray
            style={{
              position: "absolute",
              left: "10px",
              top: "8px",
              color: "#bdbdbd",
            }}
          />
        </div>
      </div>
      {busy ? (
        <Loading className={styles["loading"]} />
      ) : notes.length === 0 ? (
        <div className={styles["previous-note__no-data"]}>
          No previous notes available
        </div>
      ) : (
        <ListGroup>
          {notes.map((el) => {
            if (!el.appointment) return null;
            const sessionName = el.appointment?.appointment_type;

            return (
              <ListGroup.Item
                key={el.note_data_id}
                className={styles["previous-note__list-item"]}
              >
                <CustomSelectionControl
                  type={"radio"}
                  checked={el.note_data_id === patientNote?.note_data_id}
                  onChange={() => {
                    pickModelNote(el);
                    setError(undefined);
                    setBusy(false);
                  }}
                />
                <div
                  className={getClasses(
                    "module line-clamp-1",
                    styles["previous-note__note-name"],
                  )}
                >
                  {el.note_schema_title}
                </div>
                <div className={styles["previous-note__patient-name"]}>
                  {el.patient?.first_name || ""} {el.patient?.last_name || ""}
                </div>
                <div className={styles["previous-note__session-name"]}>
                  {sessionName}
                </div>

                <div className={styles["previous-note__session-date"]}>
                  {moment
                    .tz(el.appointment?.appointment_start, defaultUserTimezone)
                    .format(DATE_FORMAT)}
                </div>

                <div className={styles["previous-note__preview"]}>
                  <Link
                    rel="noopener noreferrer"
                    target="_blank"
                    to={`/pro-dashboard/session-review/${el.appointment.appointment_id}`}
                  >
                    <OutlineEyeOpen />
                  </Link>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}
    </div>
  );
};

export default PreviousNotesList;
